import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState('individual'); // 'individual' or 'organization'

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const { error } = await signIn(formData?.email, formData?.password);
      
      if (error) {
        setErrors({
          general: error?.message || 'Login failed. Please try again.'
        });
      } else {
        // Navigate based on user type
        const redirectPath = userType === 'organization' ?'/organization-dashboard' :'/user-dashboard';
        navigate(redirectPath);
      }
    } catch (error) {
      setErrors({
        general: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    alert('Password reset functionality would be implemented here');
  };

  const handleQuickLogin = (credentials) => {
    setFormData({
      email: credentials?.email,
      password: credentials?.password
    });
    // Auto-set user type based on email
    if (credentials?.email?.includes('corp@') || credentials?.email?.includes('admin@')) {
      setUserType('organization');
    } else {
      setUserType('individual');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white border border-gray-100 rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-coral-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Icon name="Heart" size={32} color="#14b8a6" />
          </div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-coral-600 to-orange-600 bg-clip-text text-transparent mb-2">Welcome Back</h1>
          <p className="text-gray-600">Sign in to continue your impact journey</p>
        </div>

        {/* User Type Toggle */}
        <div className="mb-6">
          <div className="flex bg-gray-50 rounded-xl p-1">
            <button
              type="button"
              onClick={() => setUserType('individual')}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                userType === 'individual' ?'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-md' :'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon name="User" size={16} className="mr-2" />
              Individual
            </button>
            <button
              type="button"
              onClick={() => setUserType('organization')}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                userType === 'organization' ?'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-md' :'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon name="Building2" size={16} className="mr-2" />
              Organization
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {errors?.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-600">{errors?.general}</p>
            </div>
          )}

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="Enter your email"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
            disabled={isLoading}
            autoComplete="email"
          />

          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter your password"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-micro"
              disabled={isLoading}
            >
              <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
            </button>
          </div>

          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => navigate('/password-recovery')}
              className="text-sm text-primary hover:text-primary/80 transition-micro font-medium"
              disabled={isLoading}
            >
              Forgot Password?
            </button>
          </div>

          <Button
            type="submit"
            fullWidth
            loading={isLoading}
            iconName="LogIn"
            iconPosition="left"
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        {/* Demo Credentials Section */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Demo Access</h3>
            <Icon name="Zap" size={16} className="text-orange-500" />
          </div>
          
          <div className="space-y-3">
            {/* Individual Demo */}
            <div className="bg-gradient-to-r from-coral-50 to-orange-50 border border-coral-200 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center mb-1">
                    <Icon name="User" size={14} className="text-coral-600 mr-2" />
                    <span className="text-xs font-medium text-coral-800">Individual Donor</span>
                  </div>
                  <p className="text-xs text-gray-600">donor@example.com</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleQuickLogin({ email: 'donor@example.com', password: 'password123' })}
                  className="text-xs border-coral-300 text-coral-700 hover:bg-coral-100"
                >
                  Use
                </Button>
              </div>
            </div>

            {/* Corporation Demo */}
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center mb-1">
                    <Icon name="Building2" size={14} className="text-teal-600 mr-2" />
                    <span className="text-xs font-medium text-teal-800">Corporation</span>
                  </div>
                  <p className="text-xs text-gray-600">corp@sustainablecorp.com</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleQuickLogin({ email: 'corp@sustainablecorp.com', password: 'password123' })}
                  className="text-xs border-teal-300 text-teal-700 hover:bg-teal-100"
                >
                  Use
                </Button>
              </div>
            </div>

            {/* Nonprofit Demo */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center mb-1">
                    <Icon name="Shield" size={14} className="text-green-600 mr-2" />
                    <span className="text-xs font-medium text-green-800">Nonprofit 501(c)(3)</span>
                  </div>
                  <p className="text-xs text-gray-600">admin@greenearth.org</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleQuickLogin({ email: 'admin@greenearth.org', password: 'password123' })}
                  className="text-xs border-green-300 text-green-700 hover:bg-green-100"
                >
                  Use
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 mb-2">Need an organization account?</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/company-register')}
              className="bg-gradient-to-r from-coral-50 to-orange-50 border-coral-300 text-coral-700 hover:bg-gradient-to-r hover:from-coral-100 hover:to-orange-100"
            >
              <Icon name="Plus" size={14} className="mr-1" />
              Register Organization
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;